import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/track";
import Logo from "@/components/Logo";
import TrackingTimeline from "@/components/TrackingTimeline";
import BreadcrumbMap from "@/components/BreadcrumbMap";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Search, PackageX } from "lucide-react";

export default function Track() {
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [tn, setTn] = useState(trackingNumber || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTrack = async (num, silent = false) => {
    if (!num) return;
    if (!silent) { setLoading(true); setError(""); setData(null); }
    try {
      const res = await api.get(`/track/${num.trim().toUpperCase()}`);
      setData(res.data);
      if (!silent) track("tracking_viewed");
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail) || "Not found");
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { if (trackingNumber) fetchTrack(trackingNumber); }, [trackingNumber]);

  useEffect(() => {
    if (!data || !["picked_up", "in_transit"].includes(data.status)) return;
    const t = setInterval(() => fetchTrack(data.tracking_number, true), 45000);
    return () => clearInterval(t);
  }, [data]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <button onClick={() => navigate("/")}><Logo /></button>
          <Button variant="outline" size="sm" onClick={() => navigate("/login")} data-testid="track-login-button">Log in</Button>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Track your parcel</h1>
        <div className="mt-4 flex gap-2">
          <Input value={tn} onChange={(e) => setTn(e.target.value)} placeholder="KC-XXXXXXXX" data-testid="track-input" />
          <Button onClick={() => { navigate(`/track/${tn.trim().toUpperCase()}`); fetchTrack(tn); }} data-testid="track-submit-button" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
            <Search className="h-4 w-4" /> Track
          </Button>
        </div>

        {loading && <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}
        {error && (
          <div className="mt-10 flex flex-col items-center gap-2 text-center text-muted-foreground" data-testid="track-error">
            <PackageX className="h-10 w-10" /> <p>{error}</p>
          </div>
        )}

        {data && (
          <Card className="mt-6" data-testid="track-result">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="font-heading text-xl">{data.tracking_number}</CardTitle>
              <StatusBadge status={data.status} testid="track-status-badge" />
            </CardHeader>
            <CardContent>
              <div className="mb-6 grid grid-cols-2 gap-4 rounded-lg bg-secondary/50 p-4 text-sm">
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">From</p><p className="font-medium">{data.pickup.city}, {data.pickup.postcode}</p>
                  {data.pickup.reference && <p className="mt-0.5 text-xs text-muted-foreground" data-testid="track-pickup-reference">Ref: {data.pickup.reference}</p>}</div>
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">To</p><p className="font-medium">{data.dropoff.city}, {data.dropoff.postcode}</p>
                  {data.dropoff.reference && <p className="mt-0.5 text-xs text-muted-foreground" data-testid="track-dropoff-reference">Ref: {data.dropoff.reference}</p>}</div>
                {data.driver_name && <div className="col-span-2"><p className="text-xs font-semibold uppercase text-muted-foreground">Courier</p><p className="font-medium">{data.driver_name}</p></div>}
              </div>
              {data.onforward && (
                <p className="mb-6 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800" data-testid="track-onforward-note">
                  This delivery has been handed to <b>{data.onforward.carrier}</b>{data.onforward.reference ? <> — quote reference <b>{data.onforward.reference}</b> with them</> : null}. It's now tracked manually with that carrier.
                </p>
              )}
              <BreadcrumbMap history={data.status_history} driverLocation={data.driver_location} />
              <TrackingTimeline history={data.status_history} />
              {data.proof_of_delivery && (
                <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm font-semibold text-emerald-800">Proof of delivery</p>
                  <p className="text-sm text-emerald-700" data-testid="track-pod-text">{data.proof_of_delivery.atl ? "Left in a safe place under the sender's Authority to Leave (ATL) — a photo of where it was left was captured" : "Signed proof of delivery captured"}{data.proof_of_delivery.captured_at ? ` — ${new Date(data.proof_of_delivery.captured_at).toLocaleString("en-AU")}` : ""}. The sender can view the full POD{data.proof_of_delivery.atl ? " photo" : ""} from their account.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
